import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import once from 'lodash/once'
import React, { ReactNode, type ChangeEvent, useCallback, FormEvent, useMemo } from 'react'
import {
  TextField,
  DatePicker,
  InputHelperText,
  PersonAutoComplete,
  Checkbox,
  Spacer,
  useBreakpoint,
  theme,
} from '@mtsdengi/ui-kit'
import { MetricsEventsEnum, useMetrics } from '@shared/utils'
import { MASKS } from '@shared/constants'
import type { IdentityClientFields } from '@shared/types'
import { Flex } from '@shared/ui/Flex'
import { identityClientForm, stores } from '../../model'

/**
 * @prop includeFields Отображаемые поля
 * @prop agreements Список соглашений
 */
export type IdentityFormProps = {
  agreements: ReactNode
  includeFields?: Partial<IdentityClientFields>
}

export const IdentityForm = ({
  includeFields: { clientFio = true, dateOfBirth = true, phoneNumber = true, email = true } = {},
  agreements,
}: IdentityFormProps) => {
  const { fields, submit } = useForm(identityClientForm)
  const { notPatronimyc } = useUnit({
    notPatronimyc: stores.$notPatronimyc,
  })
  const { isMobile } = useBreakpoint()

  const sendEventData = useMetrics()
  const handleFioBlur = useCallback(
    ({ currentTarget }: FormEvent<HTMLInputElement>) => {
      fields.clientFio.onBlur()
      if (currentTarget.value) {
        sendEventData({
          label: 'fio',
          event: MetricsEventsEnum.VALIDATE_SUCCESS_FIELD,
        })
      }
    },
    [sendEventData, fields.clientFio]
  )

  const handleBirthDateBlur = useCallback(
    ({ currentTarget }: FormEvent<HTMLInputElement>) => {
      fields.birthDate.onBlur()
      if (currentTarget.value) {
        sendEventData({
          label: 'data_rozhdeniya',
          event: MetricsEventsEnum.VALIDATE_SUCCESS_FIELD,
        })
      }
    },
    [sendEventData, fields.birthDate]
  )

  const handlePhoneNumberBlur = useCallback(
    ({ currentTarget }: FormEvent<HTMLInputElement>) => {
      fields.phoneNumber.onBlur()
      if (currentTarget.value) {
        sendEventData({
          args: { phone: currentTarget.value },
          conversions: true,
          label: 'telefon',
          event: MetricsEventsEnum.VALIDATE_SUCCESS_FIELD,
        })
      }
    },
    [sendEventData, fields.phoneNumber]
  )

  const handleEmailNumberBlur = useCallback(
    ({ currentTarget }: FormEvent<HTMLInputElement>) => {
      fields.email.onBlur()
      if (currentTarget.value) {
        sendEventData({
          conversions: true,
          label: 'email',
          event: MetricsEventsEnum.VALIDATE_SUCCESS_FIELD,
        })
      }
    },
    [sendEventData, fields.email]
  )

  const handleFormChange = useMemo(
    () =>
      once(() => {
        sendEventData({
          label: 'nachalo_zapolneniya',
          event: MetricsEventsEnum.SHOW_CONFIRMED,
          conversions: true,
        })
      }),
    [sendEventData]
  )

  return (
    <form onSubmit={() => submit()} onChange={handleFormChange}>
      <Flex isContainer columns={2} rowSpace={1} columnSpace={1}>
        {clientFio && (
          <Flex isColumn size={isMobile ? 2 : 1}>
            <PersonAutoComplete
              debounce
              label="Фамилия, имя и отчество"
              placeholder="Иванов Иван Иванович"
              size="lg"
              value={fields.clientFio.value}
              onChange={(_: ChangeEvent<HTMLInputElement>, { newValue }: { newValue: string }) => {
                fields.clientFio.onChange(newValue)
              }}
              onSelect={fields.clientFioSuggest.onChange}
              debounceDelay={300}
              hasError={fields.clientFio.hasError()}
              onBlur={handleFioBlur}
            />
            {fields.clientFio.hasError() && <InputHelperText hasError>{fields.clientFio.errorText()}</InputHelperText>}
            {notPatronimyc && <Spacer space={theme.spacings.sm} spacemob={theme.spacings.sm} />}
            {notPatronimyc && (
              <Checkbox checked variant="violet">
                Нет отчества
              </Checkbox>
            )}
          </Flex>
        )}
        {dateOfBirth && (
          <Flex isColumn size={isMobile ? 2 : 1}>
            <DatePicker
              label="Дата рождения"
              placeholder="дд.мм.гггг"
              value={fields.birthDate.value}
              onChange={fields.birthDate.onChange}
              onBlur={handleBirthDateBlur}
              hasError={fields.birthDate.hasError()}
            />
            {fields.birthDate.hasError() && <InputHelperText hasError>{fields.birthDate.errorText()}</InputHelperText>}
          </Flex>
        )}
        {phoneNumber && (
          <Flex isColumn size={isMobile ? 2 : 1}>
            <TextField
              showMask
              label="Мобильный телефон"
              placeholder="+7"
              type="tel"
              inputmode="tel"
              value={fields.phoneNumber.value}
              onChange={(evt) => fields.phoneNumber.onChange(evt.target.value)}
              onBlur={handlePhoneNumberBlur}
              hasError={fields.phoneNumber.hasError()}
              mask={MASKS.phoneNumber}
            />
            {fields.phoneNumber.hasError() && (
              <InputHelperText hasError>{fields.phoneNumber.errorText()}</InputHelperText>
            )}
          </Flex>
        )}
        {email && (
          <Flex isColumn size={isMobile ? 2 : 1}>
            <TextField
              label="Электронная почта"
              placeholder="mail@example"
              value={fields.email.value}
              onChange={(evt) => fields.email.onChange(evt.target.value)}
              onBlur={handleEmailNumberBlur}
              hasError={fields.email.hasError()}
            />
            {fields.email.hasError() && <InputHelperText hasError>{fields.email.errorText()}</InputHelperText>}
          </Flex>
        )}
      </Flex>
      <Spacer space={theme.spacings.sm} spacemob={theme.spacings.sm} />
      {agreements}
    </form>
  )
}
